import axios from "axios";
import { API_URL } from "../MasterData/index";
import { showSessionExpiredModal } from "../Utils/sessionExpiredManager";

/**
 * Get authorization token from localStorage
 * @returns {string|null} Authorization token
 */
export const getAuthToken = () => {
  return localStorage.getItem("authToken");
};

/**
 * Handle 401 Unauthorized responses
 * Shows session expired modal and logs out user
 */
const handle401Error = () => {
  // Show session expired modal
  showSessionExpiredModal();
};

/**
 * Common fetch wrapper with token and 401 handling
 * @param {string} url - API endpoint URL
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
const fetchWithAuth = async (url, options = {}) => {
  const token = getAuthToken();

  // Add authorization header if token exists
  const headers = {
    ...options.headers,
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle 401 Unauthorized
  if (response.status === 401) {
    handle401Error();
    throw new Error("Session expired");
  }

  return response;
};

/**
 * Axios instance with interceptors for token and 401 handling
 */
const axiosInstance = axios.create();

// Request interceptor to add token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle 401
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      handle401Error();
    }
    return Promise.reject(error);
  },
);

/**
 * Get bulk users/people data from the API
 * @returns {Promise<Array>} Array of users with complete details
 */
export const getBulkUsers = async () => {
  try {
    const response = await fetchWithAuth(`${API_URL}/manage-office/bulk/user`);
    const result = await response.json();
    if (result.status && result.data) {
      return result.data;
    }
    return [];
  } catch (error) {
    console.error("Error fetching bulk users:", error);
    throw error;
  }
};

/**
 * Get departments from the API
 * @returns {Promise<Array>} Array of departments
 */
export const getDepartments = async () => {
  try {
    const response = await fetchWithAuth(`${API_URL}/manage-office/department`);
    const result = await response.json();
    if (result.status && result.data) {
      return result.data;
    }
    return [];
  } catch (error) {
    console.error("Error fetching departments:", error);
    throw error;
  }
};

/**
 * Get employee types from the API
 * @returns {Promise<Array>} Array of employee types
 */
export const getEmployeeTypes = async () => {
  try {
    const response = await fetchWithAuth(
      `${API_URL}/manage-office/employeeType`,
    );
    const result = await response.json();
    if (result.status && result.data) {
      return result.data;
    }
    return [];
  } catch (error) {
    console.error("Error fetching employee types:", error);
    throw error;
  }
};

/**
 * Get designations from the API
 * @returns {Promise<Array>} Array of designations
 */
export const getDesignations = async () => {
  try {
    const response = await fetchWithAuth(
      `${API_URL}/manage-office/designation`,
    );
    const result = await response.json();
    if (result.status && result.data) {
      return result.data;
    }
    return [];
  } catch (error) {
    console.error("Error fetching designations:", error);
    throw error;
  }
};

/**
 * Add a new user/employee
 * @param {Object} userData - User data to submit
 * @returns {Promise<Object>} API response
 */
export const addUser = async (userData) => {
  try {
    const response = await fetchWithAuth(`${API_URL}/manage-office/add/user`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(userData),
    });

    // Get response details for better error handling
    const responseText = await response.text();
    let responseData;
    try {
      responseData = JSON.parse(responseText);
    } catch (e) {
      responseData = { message: responseText };
    }

    if (!response.ok) {
      throw new Error(
        `API Error (${response.status}): ${
          responseData.message || responseText || "Unknown error"
        }`,
      );
    }

    return responseData;
  } catch (error) {
    console.error("Error adding user:", error);
    throw error;
  }
};

// Update/Edit User API
export const updateUser = async (userData) => {
  try {
    const response = await fetchWithAuth(`${API_URL}/manage-office/add/user`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(userData),
    });

    // Get response details for better error handling
    const responseText = await response.text();
    let responseData;
    try {
      responseData = JSON.parse(responseText);
    } catch (e) {
      responseData = { message: responseText };
    }

    if (!response.ok) {
      throw new Error(
        `API Error (${response.status}): ${
          responseData.message || responseText || "Unknown error"
        }`,
      );
    }

    return responseData;
  } catch (error) {
    console.error("Error updating user:", error);
    throw error;
  }
};
// User Login API (no token required for login)
export const userLogin = async (requestPayload) => {
  try {
    const response = await axios.post(
      `${API_URL}/user-auth/login/1`,
      requestPayload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response;
  } catch (error) {
    console.log("Login error:", error);
    return error;
  }
};

/**
 * Get staff/user data for assignment dropdown
 * @returns {Promise<Array>} Array of staff members with userId, staffFullName, and uId
 */
export const getStaffData = async () => {
  try {
    const response = await fetchWithAuth(
      `${API_URL}/manage-office/filterData/user?fields=userId,staffFullName,uId`,
    );
    const result = await response.json();
    if (result.status && result.data) {
      return result.data;
    }
    return [];
  } catch (error) {
    console.error("Error fetching staff data:", error);
    throw error;
  }
};

// Create New Task API
export const createNewTaskAPI = async (userId, requestPayload) => {
  try {
    const apiUrl = `${API_URL}/user-task/${userId}/AddTask`;
    const response = await axiosInstance.patch(apiUrl, requestPayload, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    console.log("Error in createNewTaskAPI:", error);
    return error;
  }
};

// Update Task Status API
export const updateTaskStatusAPI = async (
  taskNumber,
  newStatus,
  sk,
  userId,
  additionalData = {},
) => {
  try {
    const response = await axiosInstance.patch(
      `${API_URL}/user-task/Update/Task`,
      {
        statusUpdateTo: newStatus,
        taskNumber: taskNumber,
        userId: userId,
        SK: sk,
        ...additionalData, // Include Points and ActualDoneDate when provided
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response;
  } catch (error) {
    console.log("Error in updateTaskStatusAPI:", error);
    throw error;
  }
};

// Get User Tasks API
export const getTasksAPI = async (userId) => {
  // Trim the userId to remove any accidental spaces
  const trimmedUserId = userId.trim();
  console.log("Fetching tasks for user:", trimmedUserId);
  try {
    const response = await axiosInstance.get(
      `${API_URL}/user-task/${trimmedUserId}/GetTask`,
    );
    return response;
  } catch (error) {
    console.log("Error in getTasksAPI:", error);
    throw error;
  }
};

/**
 * Get roles from the API
 * @returns {Promise<Array>} Array of roles
 */
export const getMasterData = async (entityType) => {
  try {
    const response = await fetchWithAuth(
      `${API_URL}/manage-office/${entityType}`,
    );
    const result = await response.json();
    if (result.status && result.data) {
      return result.data;
    }
    return [];
  } catch (error) {
    console.error(`Error fetching ${entityType}:`, error);
    throw error;
  }
};

/**
 * Get roles from the API
 * @returns {Promise<Array>} Array of roles
 */
export const getRoles = async () => {
  return getMasterData("role");
};

/**
 * Get grades from the API
 * @returns {Promise<Array>} Array of grades
 */
export const getGrades = async () => {
  return getMasterData("grade");
};

/**
 * Get levels from the API
 * @returns {Promise<Array>} Array of levels
 */
export const getLevels = async () => {
  return getMasterData("level");
};

/**
 * Create a new leave type
 * @param {Object} leaveData - Leave type data
 * @returns {Promise<Object>} API response
 */
export const createLeaveType = async (leaveData) => {
  try {
    const response = await fetchWithAuth(`${API_URL}/manage-office/leave`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(leaveData),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || "Failed to create leave type");
    }

    return result;
  } catch (error) {
    console.error("Error creating leave type:", error);
    throw error;
  }
};

// Change Password API
export const changePassword = async (userData) => {
  try {
    const response = await fetchWithAuth(
      `${API_URL}/user-auth/change-password/2`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      },
    );

    return await response.json();
  } catch (error) {
    console.error("Error changing password:", error);
    return { status: false, message: error.uiMsg };
  }
};

/**
 * Get countries from the API
 * @returns {Promise<Array>} Array of countries
 */
export const getCountries = async () => {
  try {
    const response = await fetchWithAuth(`${API_URL}/manage-office/country`);
    const result = await response.json();
    if (result.status && result.data) {
      return result.data;
    }
    return [];
  } catch (error) {
    console.error("Error fetching countries:", error);
    throw error;
  }
};

// Add this function to your API utilities

export const verifyUserId = async (userId) => {
  try {
    const response = await fetchWithAuth(
      `${API_URL}/user-auth/verify-userId/3`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId }),
      },
    );

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error verifying userId:", error);
    throw error;
  }
};

/**
 * Fetch leave types for a specific country
 * @param {string} countryCode - The country code (e.g., 'IN' for India)
 * @returns {Promise<Array>} - Array of leave types
 */
export const getLeaveTypes = async (countryCode) => {
  try {
    if (!countryCode) {
      return [];
    }

    const response = await fetchWithAuth(
      `${API_URL}/manage-office/leave?countryCode=${countryCode}`,
    );
    const result = await response.json();

    console.log("API response for leave types:", result);

    // Return the data array directly
    if (result && result.status && Array.isArray(result.data)) {
      return result.data;
    }

    return [];
  } catch (error) {
    console.error("Error fetching leave types:", error);
    return [];
  }
};

/**
 * Apply for a leave
 * @param {Object} leaveData - Leave application data
 * @returns {Promise<Object>} - API response
 */
export const applyForLeave = async (leaveData) => {
  try {
    const response = await axiosInstance.post(
      `${API_URL}/user-leave/Staff/ApplyLeave`,
      leaveData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.log("Error in applyForLeave:", error);
    return error.response ? error.response.data : error;
  }
};

/**
 * Fetch leave history for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - API response with leave history
 */
export const getUserLeaveHistory = async (userId) => {
  try {
    if (!userId) {
      throw new Error("User ID is required");
    }

    const response = await fetchWithAuth(
      `${API_URL}/user-leave/${userId}/GetLeave/`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Error fetching leave history: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("API Error in getUserLeaveHistory:", error);
    throw error;
  }
};

/**
 * Fetches people data from the API
 * @returns {Promise<Array>} Array of people with their details
 */
export const fetchPeopleData = async () => {
  try {
    console.log("Attempting to fetch people data from API...");

    const response = await fetchWithAuth(
      `${API_URL}/manage-office/filterData/user?fields=userId,staffFullName,uId`,
    );

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const result = await response.json();
    console.log("API response:", result);

    if (!result.status || !result.data) {
      throw new Error("Invalid response format or status is false");
    }

    // Transform data for use in the component
    // Map the API response fields to the expected format:
    // staffFullName -> name
    // uId -> staff_id
    // userId -> login_id
    const formattedPeople = result.data.map((person) => ({
      loginId: person.userId,
      staffId: person.uId,
      name: person.staffFullName,
      // Format display value to show name and staff ID
      displayValue: `${person.staffFullName} (${person.uId})`,
    }));

    return formattedPeople;
  } catch (error) {
    console.error("Error fetching people data:", error);
    throw error;
  }
};

/**
 * Fetch holidays and weekend configuration from API
 * @param {string} countryCode - Country code (e.g., 'IN' for India)
 * @returns {Promise<Object>} Holiday and weekend configuration data
 */
export const fetchHolidaysAPI = async (countryCode = "IN") => {
  try {
    console.log(`Fetching holidays for country code: ${countryCode}...`);

    const response = await fetchWithAuth(
      `${API_URL}/manage-office/holiday?countryCode=${countryCode}`,
    );

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const result = await response.json();
    console.log("Holiday API response:", result);

    if (!result.status || !result.data) {
      throw new Error("Invalid response format or status is false");
    }

    // Separate holidays from Saturday/Sunday configuration
    const holidays = [];
    let saturdayWorking = false;
    let sundayWorking = false;

    result.data.forEach((item) => {
      if (item.holidayStatus === "true" || item.holidayStatus === true) {
        // Check if it's Saturday or Sunday configuration
        if (item.holidayName.toLowerCase() === "saturday") {
          saturdayWorking = false; // If Saturday is in the list with holidayStatus=true, it's a non-working day
        } else if (item.holidayName.toLowerCase() === "sunday") {
          sundayWorking = false; // If Sunday is in the list with holidayStatus=true, it's a non-working day
        } else if (item.holidayStartDate) {
          // It's a public holiday with a date
          holidays.push({
            id: item.id,
            date: item.holidayStartDate,
            name: item.holidayName,
            countryCode: item.countryCode,
          });
        }
      } else {
        // If holidayStatus is false, it means it's a working day
        if (item.holidayName.toLowerCase() === "saturday") {
          saturdayWorking = true;
        } else if (item.holidayName.toLowerCase() === "sunday") {
          sundayWorking = true;
        }
      }
    });

    return {
      holidays,
      saturdayWorking,
      sundayWorking,
      countryCode,
    };
  } catch (error) {
    console.error("Error fetching holidays:", error);
    throw error;
  }
};

/**
 * Get all tasks by year and quarter
 * @param {string} year - Year (e.g., '2025')
 * @param {string} quarter - Quarter (Q1, Q2, Q3, or Q4)
 * @returns {Promise<Object>} API response with tasks data
 */
export const getAllTasksByQuarter = async (year, quarter) => {
  try {
    if (!year || !quarter) {
      throw new Error("Year and quarter are required");
    }

    const response = await fetchWithAuth(
      `${API_URL}/user-task/${year}/${quarter}`,
    );

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const result = await response.json();
    console.log("All Tasks API response:", result);

    return result;
  } catch (error) {
    console.error("Error fetching all tasks:", error);
    throw error;
  }
};

/**
 * Get employment agreement content based on role ID and country code
 * @param {string} roleId - Role ID (e.g., 'U-PT-ROLE-001')
 * @param {string} countryCode - Country code (e.g., 'IN', 'US')
 * @returns {Promise<Object>} Employment agreement content with sections
 */
export const getEmploymentAgreement = async (roleId, countryCode) => {
  try {
    if (!roleId || !countryCode) {
      throw new Error("Role ID and country code are required");
    }

    const response = await fetchWithAuth(
      `${API_URL}/manage-office/employmentAgreement?roleId=${roleId}&countryCode=${countryCode}`,
    );

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const result = await response.json();

    if (result.status && result.data && result.data.length > 0) {
      // Return the first agreement data (most recent)
      return result.data[0];
    }

    // Return null if no agreement found
    return null;
  } catch (error) {
    console.error("Error fetching employment agreement:", error);
    throw error;
  }
};

/**
 * Planner — per-employee, per-month storage.
 * Backend stores one DynamoDB row per (userId, month): R1 = userId (partition key),
 * R2 = month in "YYYY-MM" format (sort key). GET's response wraps the actual document:
 *   { status, message, data: { userId, month, createdAt, updatedAt, plannerData: { activities, weeks } } }
 * `plannerData` (the `{ activities, weeks }` shape) is the only part this app reads/writes —
 * `getPlannerMonth` unwraps it automatically, so the rest of the app never sees the envelope.
 *  - GET returns just `data.plannerData` (or null if nothing's been saved for that month yet).
 *  - POST replaces the *entire* month document. There is no per-activity endpoint — to add,
 *    edit, or remove a single activity, mutate the fetched `{ activities, weeks }` object in
 *    memory and re-POST the whole thing back.
 *  - DELETE removes the whole month's row.
 */

/**
 * Fetch one employee's planner data for a given month.
 * @param {string} userId - R1 / partition key
 * @param {string} month - R2 / sort key, format "YYYY-MM"
 * @returns {Promise<Object|null>} The stored month document, or null if none exists yet
 */
export const getPlannerMonth = async (userId, month) => {
  try {
    if (!userId || !month) {
      throw new Error("userId and month are required");
    }

    const response = await fetchWithAuth(
      `${API_URL}/user-planner/${userId}/${month}`,
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const result = await response.json();
    // The row's actual { activities, weeks } document lives under data.plannerData —
    // createdAt/updatedAt/userId/month alongside it are server-managed envelope fields.
    return result.data?.plannerData ?? null;
  } catch (error) {
    console.error("Error fetching planner month:", error);
    throw error;
  }
};

/**
 * Create or fully replace one employee's planner data for a given month.
 * Since storage is one row per month, this always sends the *whole* month's document —
 * build the complete object (existing days/activities plus your change) before calling
 * this rather than sending a partial diff, or you will overwrite the rest of the month.
 * @param {string} userId - R1 / partition key
 * @param {string} month - R2 / sort key, format "YYYY-MM"
 * @param {Object} monthData - The complete month document to store
 * @returns {Promise<Object>} The saved document as returned by the API
 */
export const savePlannerMonth = async (userId, month, monthData) => {
  try {
    if (!userId || !month) {
      throw new Error("userId and month are required");
    }

    const response = await axiosInstance.post(
      `${API_URL}/user-planner/${userId}/${month}`,
      monthData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error saving planner month:", error);
    return error.response ? error.response.data : error;
  }
};

/**
 * Delete one employee's entire planner document for a given month.
 * @param {string} userId - R1 / partition key
 * @param {string} month - R2 / sort key, format "YYYY-MM"
 * @returns {Promise<Object>} API response
 */
export const deletePlannerMonth = async (userId, month) => {
  try {
    if (!userId || !month) {
      throw new Error("userId and month are required");
    }

    const response = await axiosInstance.delete(
      `${API_URL}/user-planner/${userId}/${month}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting planner month:", error);
    return error.response ? error.response.data : error;
  }
};
